import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import i18nStore from "../../../../i18n/store";
import {
    GridActionsCellItem,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridRowId,
    GridRowsProp
} from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ControlPointDuplicateOutlinedIcon from '@mui/icons-material/ControlPointDuplicateOutlined';
import Tooltip from '@mui/material/Tooltip';
import { BranchInterface, CreateOrUpdateBranchInterface } from "../../schemas/branches";
import CustomEditNumberComponent from "../cellRenderers/CustomEditNumberComponent";
import CustomRenderCell from "../cellRenderers/CustomRenderCell";
import CustomRenderXRGBCell from "../cellRenderers/CustomRenderXRGBCell";
import IsActiveField from "../cellRenderers/IsActiveField";
import SelectNodeEditComponent from "../SelectNodeEditComponent";

export const useMuiBranchesColDefs = (
    rows: GridRowsProp,
    setRows: (rows: GridRowsProp) => void,
    showZeroSeqParamsFlag: boolean,
    handlerDeleteClick: (id: GridRowId) => void,
    handlerDuplicateClick: (row: any) => void,
    updateApiFunc: (projectId: string | number, branch: CreateOrUpdateBranchInterface) => Promise<BranchInterface>,
): GridColDef[] => {
    const translations = i18nStore.getTranslations;
    const [colDefs, setColDefs] = useState<GridColDef[]>([]);
    const handleDeleteClick = (id: GridRowId) => () => {
        handlerDeleteClick(id);
    };

    const handleDuplicateClick = (id: GridRowId) => () => {
        const duplicatedRow = rows.find((row) => row.id === id);
        handlerDuplicateClick(duplicatedRow);
    };

    useEffect(() => {
        const zeroSeqParamsCols: GridColDef[] = [
            {
                field: "is_active_0",
                type: 'boolean',               
                renderHeader: () => (
                    <Tooltip title={translations._active.isActive0Long} placement="top">
                        <Typography variant="subtitle2">
                            {translations._active.isActive0}
                        </Typography>
                    </Tooltip>
                ),
                editable: false,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <IsActiveField {...params} rows={rows} setRows={setRows} updateApiFunc={updateApiFunc} />
                },
            },
            {
                field: "R0",
                type: 'number',                
                renderHeader: () => (                    
                    <Tooltip title={translations._tablesBranchesEquip.r0Long} placement="top">
                        <Typography variant="subtitle2">
                            R0, {translations._measUnit.Om}
                        </Typography>                        
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value;
                    if (!params.row.R0 && !params.row.X0) {
                        value = params.row.R1;
                    }
                    return <CustomRenderCell {...params } value={value} realValue={params.row.R0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "X0",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.x0Long} placement="top">
                        <Typography variant="subtitle2">
                            X0, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value;
                    if (!params.row.R0 && !params.row.X0) {
                        value = params.row.X1;
                    }
                    return <CustomRenderXRGBCell {...params} value={value} realValue={params.row.X0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
            {
                field: "G0",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.g0Long} placement="top">
                        <Typography variant="subtitle2">
                            G0, {translations._measUnit.mkSm}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.G0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "B0",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.b0Long} placement="top">
                        <Typography variant="subtitle2">
                            B0, {translations._measUnit.mkSm}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderXRGBCell {...params} realValue={params.row.B0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
        ];
        const nonZeroSeqParamsCols: GridColDef[] = [
            {
                field: "R1",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.r12Long} placement="top">
                        <Typography variant="subtitle2">
                            R1 (R2), {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.R1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "X1",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.x12Long} placement="top">
                        <Typography variant="subtitle2">
                            X1 (X2), {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value;
                    if (!params.row.R1 && !params.row.X1) {
                        value = 0.001;
                    }
                    return <CustomRenderXRGBCell {...params} value={value} realValue={params.row.X1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
            {
                field: "G1",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.g12Long} placement="top">
                        <Typography variant="subtitle2">
                            G1 (G2), {translations._measUnit.mkSm}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.G1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "B1",
                type: 'number',
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.b12Long} placement="top">
                        <Typography variant="subtitle2">
                            B1 (B2), {translations._measUnit.mkSm}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderXRGBCell {...params} realValue={params.row.B1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
        ];
        const newDefColsBranch: GridColDef[] = [
            {
                field: "id",
                type: 'number',
            },
            {
                field: "is_active",
                type: 'boolean',                
                renderHeader: () => (
                    <Tooltip title={translations._active.isActiveLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._active.isActive}
                        </Typography>
                    </Tooltip>
                ),
                editable: false,
                filter: true,
                widthPart: 0.8,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <IsActiveField {...params} rows={rows} setRows={setRows} updateApiFunc={updateApiFunc} />
                },
            },
            {
                field: "name",                
                renderHeader: () => (
                    <Tooltip title={translations.nameLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations.name}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                widthPart: 2,
                headerAlign: 'center',
            },
            {
                field: "start_node_id",
                headerName: translations.node,                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.startNodeLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.startNode}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <SelectNodeEditComponent
                        {...params}
                        rows={rows}
                        setRows={setRows}
                        updateApiFunc={updateApiFunc}
                    />
                },
            },
            {
                field: "end_node_id",
                headerName: translations.node,                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.endNodeLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.endNode}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <SelectNodeEditComponent
                        {...params}
                        rows={rows}
                        setRows={setRows}
                        updateApiFunc={updateApiFunc}
                    />
                },
            },
        ] as GridColDef[];
        const actionsCol: GridColDef = {
            field: "actions",
            type: 'actions',
            headerName: "",
            sort: false,
            headerAlign: 'center',
            cellClassName: 'actions',
            widthPart: 0.8,
            getActions: ({id}): any[] => {
                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={translations.duplicate} placement="top">
                                <ControlPointDuplicateOutlinedIcon/>
                            </Tooltip>
                        }
                        label={translations.duplicate}
                        className="textPrimary"
                        onClick={handleDuplicateClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={translations.delete} placement="top">
                                <DeleteIcon/>
                            </Tooltip>
                        }
                        label={translations.delete}
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        }  as GridColDef;
        const coefCol: GridColDef[] = [
            {
                field: "start_node_u_nom",
                type: 'number',
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.startNodenominalVoltageLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.startNodenominalVoltage},<br/>{translations._measUnit.kV}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.start_node_u_nom} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "end_node_u_nom",
                type: 'number',
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.endNodenominalVoltageLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.endNodenominalVoltage},<br/>{translations._measUnit.kV}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.end_node_u_nom} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "transformer_angle",
                type: 'number',
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.transformerAngleLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.transformerAngle},<br/>{translations._measUnit.grad}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.row.transformer_angle || 0;
                    return <CustomRenderCell {...params} value={value} realValue={params.row.transformer_angle} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} l={360} />;
                },
            },
        ];
        setColDefs([
            ...newDefColsBranch,
            ...(showZeroSeqParamsFlag ? zeroSeqParamsCols : nonZeroSeqParamsCols),
            ...coefCol,
            actionsCol,
        ]);
    }, [i18nStore.getCurrentLanguageCode, rows, showZeroSeqParamsFlag]);

    return colDefs;
}
